import { useDispatch, useSelector } from "react-redux";
import { useAxiosHook } from "./useAxiosHook";
import { useBorrowerPortalHook } from "./useBorrowerPortalHook";
import { useUnderwritingHook } from "./useUnderwritingHook";
import { profileSet } from "../redux/actions/profileActions";
import { financingRequestsSet } from "../redux/reducers/financingRequestsReducer";
import { isArray, isEmpty, isFunction, isNil, size } from "lodash";
import { loanDrilldownSet } from "../redux/actions/loanDrilldownActions";
import axios from "axios";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";

const ROUTE_PREFIX = "borrower_portal";

export const useEndpointHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();
  const error = axiosError;
  const {
    borrowerPortalHookLoading,
    // updateBorrowerProfile,
    // getBorrowerFileById,
    // getBorrowerLoanFileById,
    // getApplicationLoans,
    // uploadBorrowerIdDocument,
    // signUpBorrowerHandsomeHomebuyer,
    getBorrowerProfile,
    updateBorrowerDocMetaData,
    uploadBorrowerDocuments,
    createNewApplicationDeal,
    createNewApplicationDealWithFiles,
    assignBorrowerFileToLoan,
    getApplicationLoanById,
  } = useBorrowerPortalHook();

  const { getLoanById } = useUnderwritingHook();

  const state = useSelector((state) => state);
  const dispatch = useDispatch();
  const { financingRequests, profile } = state;

  const profileDocuments = profile?.borrowerDocuments;

  const loading =
    isLoading ||
    axiosGetIsLoading ||
    axiosPostIsLoading ||
    borrowerPortalHookLoading;

  /**
   * ------- COMPLETED -----------
   */
  const getProfile = async () => {
    getBorrowerProfile({
      onSuccessFn: (data) => dispatch(profileSet(data)),
      onErrorFn: (err) => console.error("GetBorrowerProfile error:", err),
    });
  };

  const updateProfileDocuments = ({ files, onSuccessFn }) => {
    updateBorrowerDocMetaData({
      data: files, // array of updated files
      onSuccessMsg:
        size(files) > 1
          ? `${size(files)} files updated successfully`
          : "File updated successfully",
      onSuccessFn: (res) => {
        dispatch(profileSet(res));
        if (isFunction(onSuccessFn)) {
          onSuccessFn();
        }
        // onClose();
      },
    });
  };

  const uploadProfileDocuments = ({ files, onSuccessFn }) => {
    uploadBorrowerDocuments({
      files: files,
      onSuccessMsg:
        size(files) > 1
          ? `${size(files)} files uploaded successfully`
          : "File uploaded successfully",
      onSuccessFn: (data) => {
        dispatch(profileSet(data));

        if (isFunction(onSuccessFn)) {
          onSuccessFn();
        }
      },
    });
  };

  const createNewFinancingRequest = ({ data, onSuccessFn }) => {
    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      createNewApplicationDealWithFiles({
        data: data,
        onSuccessFn: (d) => {
          if (isFunction(onSuccessFn)) {
            onSuccessFn();
          }
        },
      });
    } else {
      createNewApplicationDeal({
        data: data,
        onSuccessFn: (d) => {
          if (isFunction(onSuccessFn)) {
            onSuccessFn();
          }
        },
      });
    }
  };

  const getFinancingRequestById = ({ id, onSuccessFn }) => {
    getApplicationLoanById({
      id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        if (isFunction(onSuccessFn)) {
          onSuccessFn();
        }
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  const assignProfileDocumentToLoan = ({
    loanId,
    file_storage_name,
    onSuccessFn,
  }) => {
    console.log("assignProfileDocumentToLoan", loanId, file_storage_name);
    if (isNil(loanId) || isNil(file_storage_name)) {
      console.error("Loan ID and file are required to assign document to loan");
      return;
    }

    assignBorrowerFileToLoan({
      loanId,
      file_storage_name: file_storage_name,
      onSuccessFn: () => {
        if (isFunction(onSuccessFn)) {
          onSuccessFn();
        }
      },
    });
  };

  const submitDrawRequest = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isEmpty(data) || isNil(dealId)) {
      console.error("Loan application data is missing", { data });
      return;
    }

    const endpoint = `borrower_portal/new_draw_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan Draw Request",
      payload: data,
      path: endpoint,
      formData: true,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // ------------ NEWER ----------------- //
  // ------------ NEWER ----------------- //
  // ------------ NEWER ----------------- //

  const getBorrowerFinancingApplications = async () => {
    // console.log("FETCHHHHHHHHHHHHHHHH");
    // dispatch(financingRequestsSet([DUMMY_FINANCING_REQUESTS[0]]));
    // return;
    const endpoint = `${ROUTE_PREFIX}/retrieve_borrower_pipeline`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Financing Applications",
      path: endpoint,
      cancelSource: source,
      onSuccessFn: (res) => {
        const data = res || [];
        dispatch(financingRequestsSet(data));
        // const LOANS = [DUMMY_FINANCING_REQUESTS[0], ...data];
        // dispatch(financingRequestsSet(LOANS));
      },
    });
  };

  const uploadFilesToLoan = async ({
    files,
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ files, dealId });

    const endpoint = `${ROUTE_PREFIX}/upload_loan_docs/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Documents",
      path: endpoint,
      files: files,
      formData: true, // For file uploads
      // payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createEntityWithOrWithoutFiles = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ data });
    const endpoint_FILES = `${ROUTE_PREFIX}/create_entity_with_upload`;
    const endpoint_NO_FILES = `${ROUTE_PREFIX}/create_entity`;
    const source = axios.CancelToken.source();

    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      await axiosPostRequest({
        name: "Create Entity With Files",
        payload: data,
        path: endpoint_FILES,
        formData: true,
        cancelSource: source,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    } else {
      await axiosPostRequest({
        name: "Create Entity Without Files",
        payload: data,
        path: endpoint_NO_FILES,
        cancelSource: source,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    }
  };

  const updateBorrowerEntity = async ({
    data,
    entityId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ data, entityId });
    const endpoint = `${ROUTE_PREFIX}/update_entity/${entityId}`;

    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Entity",
      payload: data,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrowerExperienceRecord = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isEmpty(data)) {
      console.error("Loan application data is missing", { data });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/create_experience_record`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Borrower Experience Record",
      payload: data,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const addBorrowerBankAccount = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ data });
    const endpoint = `${ROUTE_PREFIX}/add_bank_account`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Borrower Bank Account",
      payload: data,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBorrowerBankAccount = async ({
    data,
    id,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ data, id });
    const endpoint = `${ROUTE_PREFIX}/update_bank_account/${id}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Borrower Bank Information",
      payload: data,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Bank information updated successfully",
      onFailFn,
    });
  };

  const uploadFilesToUnderwritingRequirement = async ({
    files,
    dealId,
    requirementId,
    payload,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ files, dealId, requirementId });

    const endpoint = `${ROUTE_PREFIX}/upload_loan_docs_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Documents To UW Requirement",
      path: endpoint,
      files: files,
      formData: true, // For file uploads
      payload,
      cancelSource: source,
      // errorTesting: true,
      onSuccessFn,
      onSuccessMsg:
        size(files) > 1
          ? "Documents uploaded successfully"
          : "Document uploaded successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const addCommentToUnderwritingRequirement = async ({
    commentText,
    dealId,
    requirementId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ commentText, dealId, requirementId });
    const endpoint = `${ROUTE_PREFIX}/add_comment_to_underwriting_requirement/${dealId}/${requirementId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Comment To Underwriting Requirement",
      payload: {
        htmlString: commentText,
      },
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Comment added successfully",
      onFailFn,
    });
  };

  const addCommentToUnderwritingException = async ({
    commentText,
    dealId,
    excecptionId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ commentText, dealId, excecptionId });
    const endpoint = `${ROUTE_PREFIX}/add_comment_to_exception/${dealId}/${excecptionId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Comment To Underwriting Exception",
      payload: {
        htmlString: commentText,
      },
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Comment added successfully",
      onFailFn,
    });
  };

  return {
    loading,
    error,
    // data
    financingRequests,
    profile,
    profileDocuments,
    // requests
    getProfile,

    updateProfileDocuments,
    uploadProfileDocuments,
    createNewFinancingRequest,
    createEntityWithOrWithoutFiles,
    getFinancingRequestById,
    submitDrawRequest,
    assignProfileDocumentToLoan,
    getBorrowerFinancingApplications,
    uploadFilesToLoan,
    uploadFilesToUnderwritingRequirement,
    updateBorrowerEntity,
    addBorrowerBankAccount,
    createBorrowerExperienceRecord,
    updateBorrowerBankAccount,
    addCommentToUnderwritingRequirement,
    addCommentToUnderwritingException,
  };
};
